import {
  Button,
  Calendar,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from '@design-system';

import { CalendarIcon } from 'lucide-react';

import { useState } from 'react';
import { format } from 'date-fns';
import {
  MARITAL_STATUS_OPTIONS,
  SEX_OPTIONS,
} from '@/features/client-quick-plan';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';

const PARTNER_ELIGIBLE_STATUSES = ['Married', 'Partnered'];

interface QuickPlanPersonFieldsProps<T extends FieldValues> {
  control: Control<T>;
  fieldPrefix?: 'partner.';
  onMaritalStatusChange?: (value: string) => void;
}

export function QuickPlanPersonFields<T extends FieldValues>({
  control,
  fieldPrefix,
  onMaritalStatusChange,
}: QuickPlanPersonFieldsProps<T>) {
  const [calendarOpen, setCalendarOpen] = useState(false);

  function fieldName(name: string): FieldPath<T> {
    return (fieldPrefix ? `${fieldPrefix}${name}` : name) as FieldPath<T>;
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={control}
          name={fieldName('firstName')}
          render={({ field }) => (
            <FormItem>
              <FormLabel>First name <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input
                  placeholder="Jane"
                  {...field}
                  value={field.value as string}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name={fieldName('lastName')}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Last name <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input
                  placeholder="Doe"
                  {...field}
                  value={field.value as string}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={control}
          name={fieldName('birthDate')}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Date of birth <span className="text-destructive">*</span></FormLabel>
              <Popover
                open={calendarOpen}
                onOpenChange={setCalendarOpen}
              >
                <PopoverTrigger type="button">
                  <FormControl>
                    <Button
                      type="button"
                      variant="outline"
                      className={cn('w-full justify-start text-left font-normal', {
                        'text-muted-foreground': !field.value,
                      })}
                    >
                      <CalendarIcon />
                      {field.value ? format(field.value as Date, 'MMM d, yyyy') : 'Pick a date'}
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent
                  className="z-99 w-auto p-0"
                  align="start"
                >
                  <Calendar
                    mode="single"
                    captionLayout="dropdown"
                    selected={field.value as Date | undefined}
                    onSelect={date => {
                      field.onChange(date);
                      setCalendarOpen(false);
                    }}
                    defaultMonth={field.value as Date | undefined}
                    disabled={{ after: new Date() }}
                    startMonth={new Date(1900, 0)}
                    endMonth={new Date(new Date().getFullYear(), 0)}
                  />
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name={fieldName('sex')}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Sex <span className="text-destructive">*</span></FormLabel>
              <Select
                onValueChange={field.onChange}
                value={field.value as string}
              >
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select sex" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="z-99">
                  {SEX_OPTIONS.map(option => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={control}
        name={fieldName('smokingStatus')}
        render={({ field }) => (
          <FormItem className="space-y-2">
            <FormLabel>Smoking status <span className="text-destructive">*</span></FormLabel>
            <FormControl>
              <RadioGroup
                onValueChange={field.onChange}
                value={field.value as string}
                className="flex gap-4"
              >
                <FormItem className="flex items-center gap-2 space-y-0">
                  <FormControl>
                    <RadioGroupItem value="never" />
                  </FormControl>
                  <FormLabel className="font-normal">Never</FormLabel>
                </FormItem>
                <FormItem className="flex items-center gap-2 space-y-0">
                  <FormControl>
                    <RadioGroupItem value="former" />
                  </FormControl>
                  <FormLabel className="font-normal">Former</FormLabel>
                </FormItem>
                <FormItem className="flex items-center gap-2 space-y-0">
                  <FormControl>
                    <RadioGroupItem value="current" />
                  </FormControl>
                  <FormLabel className="font-normal">Current</FormLabel>
                </FormItem>
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={control}
          name={fieldName('zipCode')}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Zip code <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input
                  placeholder="12345"
                  maxLength={5}
                  {...field}
                  value={field.value as string}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name={fieldName('maritalStatus')}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Marital status <span className="text-destructive">*</span></FormLabel>
              <Select
                onValueChange={onMaritalStatusChange ?? field.onChange}
                value={field.value as string}
              >
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="z-99">
                  {MARITAL_STATUS_OPTIONS.map(option => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      disabled={
                        fieldPrefix === 'partner.' &&
                        !PARTNER_ELIGIBLE_STATUSES.includes(option.value)
                      }
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
